import { CdkDrag, CdkDragHandle, CdkDropList } from '@angular/cdk/drag-drop';
import { CdkVirtualForOf, CdkVirtualScrollViewport, VIRTUAL_SCROLL_STRATEGY } from '@angular/cdk/scrolling';
import { CdkCell, CdkCellDef, CdkColumnDef, CdkFooterCell, CdkFooterCellDef, CdkFooterRow, CdkFooterRowDef, CdkHeaderCell, CdkHeaderCellDef, CdkHeaderRow, CdkHeaderRowDef, CdkRow, CdkRowDef, CdkTable } from '@angular/cdk/table';
import { AsyncPipe, LowerCasePipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, Directive, ElementRef, Inject, OnDestroy, OnInit, Optional, Self, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { L10n, L10nBase } from '@skylab-angular-shared/core';
import { CompanyContextLink, MatchPipe, TranslatePipe } from '@skylab-angular-shared/pipes';
import { combineLatest, concat, fromEvent, Observable, of } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged, map, pairwise, skip, switchMap, takeUntil } from 'rxjs/operators';
import { MenuService } from '../../../menu/menu.service';
import { SortIconComponent } from '../../../sort-icon/sort-icon.component';
import { StarComponent } from '../../../star/star.component';
import { TooltipDirective } from '../../../tooltip/tooltip.directive';
import { BooleanValueAccessorDirective } from '../../../value-accessors/boolean-value-accessor.directive';
import { GridVirtualizationConfig } from '../../grid-ins.component';
import { GridCellResizeDirective } from '../../resize/grid-cell-resize.directive';
import { GridResizableDirective } from '../../resize/grid-resizable.directive';
import { HeaderResizerComponent } from '../../resize/header-resizer.component';
import { GridTemplateDirective } from '../../template/grid-template.directive';
import { TableVirtualScrollStrategy } from '../../virtualization/table-virtual-scroll-strategy.service';
import { GridTableViewComponent } from '../grid-table-view/grid-table-view.component';
import { ButtonComponent } from '../../../button/button.component';
import { IconComponent } from '../../../icon/icon.component';

@Directive({
    selector: '[sklDisableVirtualScroll]'
})
export class DisableVirtualScrollDirective {
    constructor(@Optional() @Self() table: CdkTable<unknown>) {
        if (table) {
            (table as any)._disableVirtualScrolling = true;
        }
    }
}

@Component({
    selector: 'skl-table-view-virtual',
    templateUrl: 'grid-table-view-virtual.component.html',
    styleUrls: [
        '../grid-table-view/grid-table-view.component.scss',
        './grid-table-view-virtual.component.scss'
    ],
    providers: [{
        provide: VIRTUAL_SCROLL_STRATEGY,
        useClass: TableVirtualScrollStrategy
    }],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        DisableVirtualScrollDirective, 
        CompanyContextLink, 
        CdkVirtualScrollViewport, 
        CdkTable, 
        GridResizableDirective, 
        CdkDropList, 
        CdkColumnDef, 
        CdkHeaderCellDef, 
        CdkHeaderCell, 
        CdkDrag, 
        TooltipDirective, 
        CdkDragHandle, 
        NgTemplateOutlet, 
        HeaderResizerComponent, 
        SortIconComponent, 
        CdkCellDef, 
        CdkCell, 
        GridCellResizeDirective, 
        CdkFooterCellDef, 
        CdkFooterCell, 
        CdkHeaderRowDef, 
        CdkHeaderRow, 
        CdkRowDef, 
        CdkVirtualForOf, 
        CdkRow, 
        CdkFooterRowDef, 
        CdkFooterRow, 
        GridTemplateDirective, 
        BooleanValueAccessorDirective, 
        FormsModule, 
        StarComponent, 
        RouterLink, 
        AsyncPipe, 
        LowerCasePipe, 
        MatchPipe, 
        TranslatePipe,
        ButtonComponent,
        IconComponent
    ]
})
export class GridTableViewVirtualComponent<TEntity, TColumnIdentity, TSortingIdentity> extends GridTableViewComponent<TEntity, TColumnIdentity, TSortingIdentity> implements OnInit, OnDestroy {
    private static readonly BUFFER_SIZE = 10;
    private static readonly ROW_HEIGHT = 60;
    private static readonly HEADER_HEIGHT = 60;
    private static readonly FOOTER_HEIGHT = 60;
    private static readonly NO_VALUE_ROW_LENGTH = 1;
    private static readonly SCROLL_BOTTOM_OFFSET = 20;

    private virtualGridElement!: HTMLElement;
    public virtualRowsData$!: Observable<TEntity[]>;
    public scrollBottomOffset = this.config.scrollBottomOffset;

    @ViewChild(CdkVirtualScrollViewport) public virtualScrollRef!: CdkVirtualScrollViewport;

    public constructor(
        @Inject(L10n) protected override l10n: L10nBase,
        protected override menuService: MenuService,
        protected override changeDetection: ChangeDetectorRef,
        protected override elementRef: ElementRef<HTMLElement>,
        @Inject(VIRTUAL_SCROLL_STRATEGY) private readonly scrollStrategy: TableVirtualScrollStrategy
    ) {
        super(l10n, menuService, changeDetection, elementRef);
    }

    public override ngOnInit(): void {
        super.ngOnInit();
        this.virtualGridElement = this.elementRef.nativeElement;

        this.scrollStrategy.setViewportConfig(this.config);
        this.initVirtualRows();
        if (this.config.scrollToBottomAfterDataChanged) {
            this.scrollToBottomAfterDataChanged();
        }
        this.initStyleSizes(this.config);

        this.displayedColumns$.pipe(
            skip(1),
            delay(1),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            const header = document.querySelector('[cdk-header-row]') as HTMLElement;
            const footer = document.querySelector('[cdk-footer-row]') as HTMLElement;
            if (header) header.style.top = this.stickyHeaderTop;
            if (footer) footer.style.bottom = this.stickyFooterBottom;
        });

        this.dataSource$.pipe(
            distinctUntilChanged(),
            takeUntil(this.destroy$),
        ).subscribe(_ => {
            this.checkScrollOverflowY();

            const height = this.viewportHeight;
            height !== null
                ? this.virtualGridElement.style.setProperty('--virtual-grid-height', `${height + this.config.scrollBottomOffset}px`)
                : this.virtualGridElement.style.removeProperty('--virtual-grid-height');

            const maxHeight = this.maxViewportHeight;
            maxHeight !== null
                ? this.virtualGridElement.style.setProperty('--virtual-grid-max-height', `${maxHeight + this.config.scrollBottomOffset}px`)
                : this.virtualGridElement.style.removeProperty('--virtual-grid-max-height');
        });
    }

    public override ngAfterViewInit(): void {
        super.ngAfterViewInit();
        this.refreshDataAfterScroll();
    }


    public override ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    public override setGridApi(): void {
        super.setGridApi();

        if (this.options?.api?.gridControl) {
            this.options.api.gridControl.scrollToBottom = () => {
                this.scrollStrategy.scroll({behavior: 'smooth', bottom: 0});
            };
        }
    }

    private initStyleSizes(config: Required<GridVirtualizationConfig>): void {
        this.virtualGridElement.style.setProperty('--row-height', `${config.rowHeight}px`);
        this.virtualGridElement.style.setProperty('--header-height', `${config.headerHeight}px`);
    }

    private refreshDataAfterScroll(): void {
        let scrolling = false;
        const virtualScrollElement = this.virtualScrollRef.elementRef.nativeElement;
        const vseMouseUp$ = fromEvent(virtualScrollElement, 'mouseup');
        const vseScroll$ = fromEvent(virtualScrollElement, 'scroll', { passive: true });
        const vseWheel$ = fromEvent(virtualScrollElement, 'wheel', { passive: true });

        vseMouseUp$.pipe(
            takeUntil(this.destroy$),
        ).subscribe(() => {
            if (scrolling) {
                scrolling = false;
                this.changeDetection.detectChanges();
            }
        });

        vseScroll$.pipe(
            debounceTime(200),
            takeUntil(this.destroy$),
        ).subscribe(() => {
            scrolling = true;
        });

        vseWheel$.pipe(
            switchMap(e => concat(
                of({ kind: 'pending', event: e }),
                of({ kind: 'finish'}).pipe(delay(200))
            )),
            takeUntil(this.destroy$),
        ).subscribe(val => {
            if (val.kind === 'finish') {
                this.changeDetection.detectChanges();
            }
        });
    }

    private checkScrollOverflowY(): void {
        const maxHeight = this.maxViewportHeight;
        const height = this.viewportHeight;
        if (maxHeight !== null && height !== null) {
            height < maxHeight
                ? this.virtualGridElement.style.setProperty('--virtual-scroll-overflow-y', 'hidden')
                : this.virtualGridElement.style.removeProperty('--virtual-scroll-overflow-y');
        }
    }

    private initVirtualRows(): void {
        this.virtualRowsData$ = combineLatest([this.dataSource$, this.scrollStrategy.scrolledIndexChange]).pipe(
            distinctUntilChanged(),
            map(value => {
                const [ data, currentIndex ] = value;
                const start = Math.max(0, currentIndex - this.config.bufferSize);
                const end = Math.min(data.length, currentIndex + this.range + this.config.bufferSize);

                return data.slice(start, end);
            })
        );
    }

    private scrollToBottomAfterDataChanged(): void {
        this.dataSource$.pipe(
            pairwise(),
            delay(50),
            takeUntil(this.destroy$)
        ).subscribe(elm => {
            const [prev, curr] = elm;
            const max = this.config.maxRows;
            if (max !== null && (prev.length < curr.length) && (curr.length > max)) {
                this.scrollStrategy.scroll({behavior: 'smooth', bottom: 0});
            }
        });
    }

    private get config(): Required<GridVirtualizationConfig> {
        return {
            bufferSize: GridTableViewVirtualComponent.BUFFER_SIZE,
            rowHeight: GridTableViewVirtualComponent.ROW_HEIGHT,
            headerHeight: this.displayHeaders ? GridTableViewVirtualComponent.HEADER_HEIGHT : 0,
            footerHeight: GridTableViewVirtualComponent.FOOTER_HEIGHT,
            scrollBottomOffset: GridTableViewVirtualComponent.SCROLL_BOTTOM_OFFSET,
            maxRows: null,
            scrollToBottomAfterDataChanged: false,
            ...this.options?.virtualizationConfig
        };
    }

    private get range(): number {
        return this.config.maxRows || Math.floor((this.viewportOffsetHeight - this.config.headerHeight - this.footerHeight) / this.config.rowHeight);
    }

    private get viewportOffsetHeight(): number {
        const gridRect = this.virtualGridElement.getBoundingClientRect();
        return gridRect.height;
    }

    public get viewportHeight(): number | null {
        if (this.config.maxRows === null) {
            return null;
        }

        const maxVal = this.config.maxRows;
        if (this.dataSource$.value.length >= maxVal) {
            return this.maxViewportHeight;
        }

        const length = !this.options.hideNoDataRow && this.noDataCheck ? GridTableViewVirtualComponent.NO_VALUE_ROW_LENGTH : this.dataSource$.value.length;
        return this.getCalculatedHeightForRowsLimitGridType(length);
    }

    public get maxViewportHeight(): number | null {
        if (this.config.maxRows === null) return null;

        return this.getCalculatedHeightForRowsLimitGridType();
    }

    private getCalculatedHeightForRowsLimitGridType(rowsLength: number = this.config.maxRows ?? 0) {
        return (rowsLength * this.config.rowHeight) + this.config.headerHeight + this.footerHeight;
    }

    public get stickyHeaderTop(): string {
        return `-${this.scrollStrategy.inverseOfTranslation}px`;
    }

    public get stickyFooterBottom(): string {
        const offset = -1;
        return `${this.scrollStrategy.inverseOfTranslation + offset}px`;
    }

    public get footerHeight(): number {
        const height = this.isFooterVisible ? this.config.footerHeight : 0;
        this.virtualGridElement.style.setProperty('--footer-height', `${height}px`);
        return height;
    }

    //FIXME: tymczasowo, coś jest nie tak z przeliczaniem grida, i jak nie ma pozycji żadnej, to noDataRow jest niewidoczny (wskakuje pod inny content) - do poprawienia w obliczeniach gridowych
    public get noDataRowHeight(): string {
        return `-${this.config.rowHeight}px`;
    }
}
